import React, {useCallback, useEffect, useRef} from 'react'

const useContinuousScroll = (
  ref: React.RefObject<HTMLElement>
): {
  start: (dir: 'l' | 'r') => void
  stop: () => void
} => {
  const shouldScrollRef = useRef(false)

  const start = useCallback(
    (dir: 'l' | 'r') => {
      shouldScrollRef.current = true
      const tStart = performance.now()
      let tPrev = tStart
      const fn = (tNew: number) => {
        if (ref.current && shouldScrollRef.current) {
          // short scroll -- slow speed, long scroll -- fast speed
          const v = tNew - tStart > 750 ? 1.5 : 0.5
          const ds = (tNew - tPrev) * v
          tPrev = tNew
          ref.current.scrollBy(dir === 'l' ? -ds : ds, 0)
          requestAnimationFrame(fn)
        }
      }
      requestAnimationFrame(fn)
    },
    [ref, shouldScrollRef]
  )

  const stop = useCallback(() => {
    shouldScrollRef.current = false
  }, [shouldScrollRef])

  return {start, stop}
}

/**
 * Scroll provided element by X axis
 *
 * @param ref Reference to an element
 * @param mul Width multiplier to scroll by, e.g. 0.7 = 70% width is scrolled; between 0 and 1
 */
const useXScrollOnce = (
  ref: React.RefObject<HTMLElement>,
  mul: number
): ((dir: 'l' | 'r') => void) =>
  useCallback(
    (dir: 'l' | 'r') => {
      if (ref.current) {
        const {width} = ref.current.getBoundingClientRect()
        mul = Math.max(0, Math.min(1, mul))
        let left = dir === 'l' ? -mul * width : mul * width

        // compensate smooth scroll calc errors (~0.5px each time)
        const newLeft = ref.current.scrollLeft + left
        const newRight = newLeft + width
        const error = 5 // make error larger than 0.5px just in case
        if (Math.abs(newLeft) < error) {
          left -= error
        } else if (Math.abs(ref.current.scrollWidth - newRight) < error) {
          left += error
        }

        ref.current.scrollBy({left, behavior: 'smooth'})
      }
    },
    [ref]
  )

export const useBoardTabsButtonScroll = (
  ref: React.RefObject<HTMLElement>
): {
  onDown: (dir: 'l' | 'r') => void
  onUp: (dir: 'l' | 'r') => void
} => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const scrollTypeRef = useRef<'once' | 'scroll'>('once')

  const onScrollOnce = useXScrollOnce(ref, 0.7)
  const {start: startScroll, stop: stopScroll} = useContinuousScroll(ref)

  useEffect(
    () => () => timeoutRef.current && clearTimeout(timeoutRef.current),
    [timeoutRef]
  )

  const onDown = useCallback(
    (dir: 'l' | 'r') => {
      scrollTypeRef.current = 'once'
      timeoutRef.current = setTimeout(() => {
        scrollTypeRef.current = 'scroll'
        startScroll(dir)
      }, 150)
    },
    [scrollTypeRef, timeoutRef, startScroll]
  )

  const onUp = useCallback(
    (dir: 'l' | 'r') => {
      timeoutRef.current && clearTimeout(timeoutRef.current)
      if (scrollTypeRef.current === 'once') {
        onScrollOnce(dir)
      } else {
        stopScroll()
      }
    },
    [timeoutRef, scrollTypeRef, onScrollOnce, stopScroll]
  )

  return {onDown, onUp}
}
